/**
 * Subjects Store
 * Manages subjects data for filters
 * Separated from monolithic store following SOLID principles
 */

import { create } from 'zustand';
import { fetchSubjects } from '../services/subjects.service';
import { globalConfig } from '../config/globalConfig';
import type { SubjectsStore } from '../interfaces/storeInterfaces/subjectsStore';

export const useSubjectsStore = create<SubjectsStore>((set, get) => ({
  // Initial State
  subjects: [],
  allSubjects: [],
  isLoading: false,
  error: null,

  // Actions
  load: async (gradeId?: string | null, languageIsoCode?: string | null) => {
    // Clear stale data when starting a new load to prevent race conditions
    set({ isLoading: true, error: null, allSubjects: [], subjects: [] });
    try {
      const params: any = { page_size: globalConfig.defaultPageSize };
      if (!gradeId) {
        set({ isLoading: false });
        return;
      }
      if (gradeId) {
        params.grade_id = gradeId;
      }
      if (languageIsoCode) {
        params.language_iso_code = languageIsoCode;
      }
      const response = await fetchSubjects(params);
      set({ 
        allSubjects: response.data,
        subjects: response.data,
        isLoading: false 
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load subjects';
      set({ error: message, isLoading: false });
      console.error('Failed to load subjects:', error);
    }
  },

  filterByGradeAndLanguage: (gradeId: string | null, languageIsoCode: string | null) => {
    const { allSubjects } = get();
    
    let filtered = allSubjects;
    
    if (gradeId) {
      filtered = filtered.filter((subject) => subject.grade_id === gradeId);
    }
    
    if (languageIsoCode) {
      // Case-insensitive comparison for language ISO code
      const languageLower = languageIsoCode.toLowerCase();
      filtered = filtered.filter((subject) => 
        subject.language_iso_code && 
        subject.language_iso_code.toLowerCase() === languageLower
      );
    }
    
    set({ subjects: filtered });
  },

  getAvailableLanguagesForGrade: (gradeId: string | null) => {
    const { allSubjects } = get();
    
    const subjectsForGrade = gradeId
      ? allSubjects.filter((subject) => subject.grade_id === gradeId && subject.language_iso_code)
      : allSubjects.filter((subject) => subject.language_iso_code);
    
    const uniqueLanguages = Array.from(
      new Set(
        subjectsForGrade
          .map((subject) => subject.language_iso_code)
          .filter((code): code is string => Boolean(code))
      )
    );
    
    return uniqueLanguages;
  },

  reset: () => set({ subjects: [], allSubjects: [], isLoading: false, error: null }),
}));
